import {
  Content,
  Icon,
  Portal,
  SelectProps as RadixSelectProps,
  Root,
  ScrollDownButton,
  ScrollUpButton,
  SelectTriggerProps,
  Trigger,
  Value,
  Viewport,
} from '@radix-ui/react-select';
import { ReactNode } from 'react';
import tw from 'twin.macro';
import 'styled-components/macro';

import { ReactComponent as ChevronDownIcon } from 'component-lib/atoms/icon-v2/ChevronDownIcon.svg';

const ChevronUpIcon = () => <ChevronDownIcon tw="rotate-180" />;

export interface SelectProps<T extends string = string>
  extends Omit<RadixSelectProps, 'value' | 'defaultValue' | 'onValueChange'> {
  'aria-label'?: string;
  /**
   * Placeholder element to show on the trigger when no value is selected.
   */
  placeholder?: ReactNode;
  /**
   * Custom element to show inside the trigger. This overrides the placeholder.
   */
  triggerElement?: ReactNode;
  hasError?: boolean;
  className?: string;
  onBlur?: SelectTriggerProps['onBlur'];

  // Overriding for generic support
  value?: T;
  defaultValue?: T;
  onValueChange?(value: T): void;

  /**
   * Forces the dropdown to be equal width to the control
   */
  dropdownEqualWidth?: boolean;
}

export const Select = <T extends string = string>({
  'aria-label': ariaLabel,
  value,
  placeholder,
  triggerElement,
  hasError,
  className,
  children,
  disabled,
  onBlur,
  dropdownEqualWidth,
  ...props
}: SelectProps<T>) => (
  /**
   * "" typically means "no value" but radix tries to find an item with this value.
   * Reverting this to undefined makes it use the placeholder instead.
   *
   * Trying to clear the value still won't return the placeholder
   * See relevant issue: https://github.com/radix-ui/primitives/issues/1569
   */
  <Root disabled={disabled} {...props} value={value || undefined}>
    <Trigger
      // The \200b is to ensure that the select element will always have height, even if the label is blank
      // Not sure how to make it work in unicode format so disabling the octal rule
      // eslint-disable-next-line no-octal-escape
      tw="
        inline-flex
        items-center
        justify-between
        gap-1

        border
        border-gray-15
        bg-white
        rounded-lg
        px-2
        py-1

        text-primary-medium
        leading-snug
        [span]:empty:before:content-['\200b']

        transition
        duration-fast

        focus:(outline-none text-indigo)
        hover:text-indigo
        [&[data-state='open'] > .icon]:(transform -rotate-180)
        data-[disabled]:(pointer-events-none text-gray-50)
        data-[placeholder]:text-gray-50"
      css={[
        hasError
          ? tw`border-error ring-4 ring-error-lightest`
          : tw`focus:(border-secondary ring-4 ring-secondary-lighter)`,
      ]}
      aria-label={ariaLabel}
      className={className}
      onBlur={onBlur}
    >
      <Value placeholder={placeholder} children={triggerElement} />

      <Icon tw="text-gray-50 duration-fast ease-in-out transition" className="icon">
        <ChevronDownIcon />
      </Icon>
    </Trigger>

    <Portal>
      {/* Animation not possible until https://github.com/radix-ui/primitives/issues/1893 */}
      <Content
        tw="z-overlay bg-white overflow-hidden shadow-card rounded-lg"
        position="popper"
        sideOffset={10}
        css={[
          /**
           * var(--radix-select-trigger-width) required for popper sizing
           * See: https://www.radix-ui.com/docs/primitives/components/select#constrain-the-content-size
           */
          dropdownEqualWidth &&
            tw`[width: var(--radix-select-trigger-width)] [max-height: var(--radix-select-content-available-height)]`,
        ]}
      >
        <ScrollUpButton tw="flex items-center justify-center h-3 bg-white text-primary cursor-default">
          <ChevronUpIcon />
        </ScrollUpButton>

        <Viewport tw="py-[0.75rem]">{children}</Viewport>

        <ScrollDownButton tw="flex items-center justify-center h-3 bg-white text-primary cursor-default">
          <ChevronDownIcon />
        </ScrollDownButton>
      </Content>
    </Portal>
  </Root>
);
